import { useState, useRef, useEffect } from "react";
import { useT } from "./i18n";
import { speak, getSpeakLangCode } from "./tts";
import { isolateInterpolation } from "./utils";
import { TIERS, ROUNDS, QUESTIONS_PER_ROUND, START_TIER_INDEX, buildRound, nextTier, pickStartingTopic } from "./placementTest";
import { translatePlacementItems } from "./api";
import { getPlacementTranslationCache, setPlacementTranslationCache } from "./firebase";
import { ACTIVELY_TRANSLATED_LANGS } from "./constants";

// Resolves a freshly-built round's word/grammar answer text into baseLanguage, cache-first
// per item (see buildWordQuestion/grammar-question construction in placementTest.js for why
// choiceItems/promptItem exist alongside the English answer/options/prompt fields — those
// English fields are left untouched here so an English learner, or a cache/translation
// failure, always still gets a fully working round).
async function translatePlacementRound(questions, baseLanguage, langCode) {
  if (baseLanguage === "English") return questions;

  const targets = [];
  for (const q of questions) {
    if ((q.source === "word" || q.source === "listening") && q.choiceItems) {
      // Both question types translate the same shape of item (word_id -> English text) and
      // share the "word_" cache-key prefix on purpose: a word appearing in both a word-
      // translation question and a listening question within the same round is translated
      // (and cached) exactly once instead of twice.
      for (const c of q.choiceItems) targets.push({ id: `word_${c.id}`, text: c.text });
    } else if (q.source === "grammar" && q.promptItem) {
      targets.push({ id: `grammar_${q.promptItem.id}`, text: q.promptItem.text });
    }
  }
  const uniqueTargets = [...new Map(targets.map(t => [t.id, t])).values()];
  if (!uniqueTargets.length) return questions;

  const cacheKey = (id) => `${langCode}_${id}_${baseLanguage}`;
  const resultMap = new Map();
  const misses = [];
  await Promise.all(uniqueTargets.map(async (target) => {
    const cached = await getPlacementTranslationCache(cacheKey(target.id)).catch(() => null);
    if (cached) resultMap.set(target.id, cached);
    else misses.push(target);
  }));

  if (misses.length) {
    if (!ACTIVELY_TRANSLATED_LANGS.includes(baseLanguage)) {
      console.warn(`Placement test: translation generation skipped for '${baseLanguage}' (frozen/legacy language, not actively translated)`);
    } else {
      try {
        const translated = await translatePlacementItems(misses, baseLanguage);
        for (const item of translated) {
          if (!item?.id || !item?.text) continue;
          resultMap.set(item.id, item.text);
          setPlacementTranslationCache(cacheKey(item.id), item.text).catch(() => {});
        }
      } catch (err) {
        console.error(`Placement test: translation unavailable for ${baseLanguage}, falling back to English`, err);
      }
    }
  }

  return questions.map((q) => {
    if ((q.source === "word" || q.source === "listening") && q.choiceItems) {
      const options = q.choiceItems.map((c) => resultMap.get(`word_${c.id}`) || c.text);
      const correctItem = q.choiceItems.find((c) => c.id === q.wordId);
      const answer = (correctItem && resultMap.get(`word_${correctItem.id}`)) || q.answer;
      return { ...q, options, answer };
    }
    if (q.source === "grammar" && q.promptItem) {
      const prompt = resultMap.get(`grammar_${q.promptItem.id}`) || q.prompt;
      return { ...q, prompt };
    }
    return q;
  });
}

// Reused end-to-end from onboarding (origin="onboarding") and from Settings
// (origin="retake") — origin only affects what the caller does with
// onExit/onComplete, never anything rendered in here.
export function PlacementTestView({ profile, onExit, onComplete }) {
  const { t } = useT();
  const baseLanguage = profile?.baseLanguage || "English";
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
  const speakLang = getSpeakLangCode(langCode);
  const [phase, setPhase] = useState("offer"); // offer | loading | running | results
  const [tier, setTier] = useState(TIERS[START_TIER_INDEX]);
  const [round, setRound] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [pickedIndex, setPickedIndex] = useState(null);
  const [roundCorrect, setRoundCorrect] = useState(0);
  const [finalTier, setFinalTier] = useState(null);
  const usedWordIds = useRef(new Set());
  const usedGrammarIds = useRef(new Set());
  const completedRef = useRef(false);

  const q = questions[qIdx];

  useEffect(() => {
    if (phase === "running" && q?.source === "listening") {
      speak(q.prompt, q.wordId, speakLang, "word");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, round, qIdx]);

  useEffect(() => {
    if (phase !== "results" || completedRef.current) return;
    completedRef.current = true;
    onComplete({
      level: finalTier,
      startTopic: pickStartingTopic(finalTier, langCode),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, finalTier]);

  const beginRound = async (tierValue) => {
    setPhase("loading");
    const raw = buildRound(tierValue, usedWordIds.current, usedGrammarIds.current, langCode);
    const translated = await translatePlacementRound(raw, baseLanguage, langCode).catch((err) => {
      console.error("Placement test: round translation failed, showing English", err);
      return raw;
    });
    setQuestions(translated);
    setQIdx(0);
    setPickedIndex(null);
    setRoundCorrect(0);
    setPhase("running");
  };

  const start = () => { beginRound(tier); };

  // Correctness/selection are keyed off the option's position, not its display text: word and
  // listening questions both carry answerIndex (see buildWordQuestion/buildListeningQuestion in
  // placementTest.js — needed because two distinct English distractors can translate to the same
  // base-language word). Grammar questions don't set answerIndex; their options are unique
  // Turkish surface forms by construction, so plain string equality is still exactly right there.
  // The 5th "I'm not sure" option (rendered below, not part of q.options) is never correct —
  // its index (q.options.length) never matches a real answerIndex/option, so it falls through
  // to "incorrect" here with no special-casing needed.
  const isCorrectIndex = (i) => q.answerIndex != null ? i === q.answerIndex : q.options[i] === q.answer;

  const pick = (i) => {
    if (pickedIndex !== null) return;
    setPickedIndex(i);
    if (isCorrectIndex(i)) setRoundCorrect(c => c + 1);
  };

  const next = () => {
    const isLastInRound = qIdx + 1 >= questions.length;
    if (!isLastInRound) {
      setQIdx(i => i + 1);
      setPickedIndex(null);
      return;
    }
    const updatedTier = nextTier(tier, roundCorrect, questions.length);
    const isLastRound = round + 1 >= ROUNDS;
    if (isLastRound) {
      setFinalTier(updatedTier);
      setPhase("results");
      return;
    }
    setTier(updatedTier);
    setRound(r => r + 1);
    beginRound(updatedTier);
  };

  if (phase === "offer") {
    return (
      <div className="gl-view">
        <div className="lesson-header">
          <button className="back-btn" onClick={onExit}>{t("lesson.backBtn")}</button>
          <div className="lesson-title">{t("placementTest.title")}</div>
        </div>
        <div className="gl-practice-card">
          <div className="gl-practice-summary">{t("placementTest.offerTitle")}</div>
          <div style={{ fontSize: "0.875rem", color: "#8B7355", marginBottom: 20, lineHeight: 1.6 }}>
            {t("placementTest.offerBody")}
          </div>
          <button className="primary-btn" onClick={start}>{t("placementTest.startBtn")}</button>
          <button className="settings-cancel-btn" style={{ marginTop: 10 }} onClick={onExit}>{t("placementTest.notNowBtn")}</button>
        </div>
      </div>
    );
  }

  if (phase === "loading") {
    return (
      <div className="gl-view">
        <div className="lesson-header">
          <button className="back-btn" onClick={onExit}>{t("lesson.backBtn")}</button>
          <div className="lesson-title">{t("placementTest.title")}</div>
        </div>
        <div className="gl-practice-card" style={{ textAlign: "center", padding: "24px 0" }}>
          <div className="spinner" style={{ margin: "0 auto 10px" }} />
          <div style={{ fontSize: "0.8125rem", color: "#8B7355" }}>{t("placementTest.preparingRound")}</div>
        </div>
      </div>
    );
  }

  if (phase === "results") {
    return (
      <div className="gl-view">
        <div className="gl-practice-card">
          <div className="gl-practice-summary">{t("placementTest.resultsTitle")}</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#7B1E1E", margin: "8px 0 16px" }}>
            {isolateInterpolation(t("placementTest.resultsLevel"), "level", finalTier)}
          </div>
          <div style={{ fontSize: "0.875rem", color: "#8B7355", marginBottom: 12, lineHeight: 1.6 }}>
            {t("placementTest.resultsBody")}
          </div>
          <button className="primary-btn" onClick={onExit}>{t("placementTest.continueBtn")}</button>
        </div>
      </div>
    );
  }

  if (!q) return null;
  const isLastQuestion = round + 1 >= ROUNDS && qIdx + 1 >= questions.length;

  return (
    <div className="gl-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={onExit}>{t("lesson.backBtn")}</button>
        <div className="lesson-title">{t("placementTest.title")}</div>
      </div>
      <div className="gl-practice-card">
        <div className="gl-practice-progress">
          {t("placementTest.roundLabel", { round: round + 1, totalRounds: ROUNDS })} · {t("placementTest.questionLabel", { n: qIdx + 1, total: QUESTIONS_PER_ROUND })}
        </div>

        {q.source === "word" && (
          <div className="gl-practice-prompt">
            {t("placementTest.wordPrompt")}<br />
            <em><bdi dir="auto">{q.prompt}</bdi></em>
          </div>
        )}

        {q.source === "listening" && (
          <div className="gl-practice-prompt">
            {/* Icon + label are one row, one clickable button, above the question — not
                .tts-btn (a fixed 42x42 icon-only circle; icon+label would overlap in it). */}
            <div style={{ marginBottom: 10 }}>
              <button type="button" className="gl-practice-replay-btn" onClick={() => speak(q.prompt, q.wordId, speakLang, "word")}>
                🔊 <span>{t("placementTest.listeningReplay")}</span>
              </button>
            </div>
            {t("placementTest.listeningPrompt")}
          </div>
        )}

        {q.source === "grammar" && (
          <div className="gl-practice-prompt">
            {t("grammarLab.practiceQuestion")}<br />
            <em><bdi>"{q.prompt}"</bdi></em>
          </div>
        )}

        <div className="gl-practice-subtitle">{t("placementTest.notSureHint")}</div>

        <div className="gl-practice-options">
          {q.options.map((opt, i) => {
            let cls = "gl-practice-option";
            if (pickedIndex !== null) {
              if (isCorrectIndex(i)) cls += " gl-practice-option--correct";
              else if (i === pickedIndex) cls += " gl-practice-option--incorrect";
              else cls += " gl-practice-option--disabled";
            }
            return <button key={i} className={cls} onClick={() => pick(i)} dir="auto">{opt}</button>;
          })}
          {(() => {
            // 5th option, present for every question type. Never a correct answer — see
            // isCorrectIndex's comment above for why q.options.length is a safe sentinel index.
            const notSureIndex = q.options.length;
            let cls = "gl-practice-option";
            if (pickedIndex !== null) {
              cls += notSureIndex === pickedIndex ? " gl-practice-option--incorrect" : " gl-practice-option--disabled";
            }
            return <button key="not-sure" className={cls} onClick={() => pick(notSureIndex)}>{t("placementTest.notSureOption")}</button>;
          })()}
        </div>

        {pickedIndex !== null && (
          <>
            <div className={`gl-practice-feedback ${isCorrectIndex(pickedIndex) ? "gl-practice-feedback--correct" : "gl-practice-feedback--incorrect"}`}>
              {isCorrectIndex(pickedIndex) ? t("grammarLab.practiceCorrectFeedback") : isolateInterpolation(t("grammarLab.practiceIncorrectFeedback"), "answer", q.answer)}
            </div>
            <button className="primary-btn" style={{ marginTop: 14 }} onClick={next}>
              {isLastQuestion ? t("placementTest.finishBtn") : t("grammarLab.practiceNext")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
